/* eslint-disable @typescript-eslint/naming-convention */
/**
 * `@typescript-eslint/naming-convention` is overridden in this file as this is
 * duplicated/imported code from chrome-integrations repo, where some generic
 * names begin with letters other than `T`.
 */

import { ampli } from 'lib-shared-tracking';
import type {
  ConversationHistory,
  ReadConversationApiArg,
} from 'lib-shared-types-api';
import { DeepNonNullable, DeepPick } from 'ts-essentials';


import { Maybe } from '../apis/graphQl/elevio/grappa/types/grappa.types';
import { ConversationThread, Thread } from '../types/messages';
import {
  removeAnchorFromArticleId,
  removeAnchorFromMultipleArticleIds,
} from '../utils';

import { trackSelfDescribingEvent } from './snowcat';

type TicketSubject = string | null;
type CommonEventParams = {
  url: string | undefined;
  languageId: string | undefined;
};

export type TicketResolutionStartEvent = CommonEventParams & {
  ticketId: string;
};

type MainConversation = DeepNonNullable<ConversationHistory>;
type ConversationEmail = DeepPick<
  MainConversation,
  {
    main_conversation: {
      assignment: {
        agent: {
          email;
        };
      };
    };
  }
>;
export type ConversationGetSucceededEvent = {
  meta: ReadConversationApiArg;
  data: {
    history: ConversationEmail;
  };
};

export type SearchInputValueUpdatedAction = {
  value: string;
  conversationId: number;
};

/**
 * This is triggered when the completion workflow begins.
 *
 * @param ticketId
 */
export function ticketResolutionStart({
  ticketId,
  url,
  languageId,
}: TicketResolutionStartEvent): void {
  trackSelfDescribingEvent(
    'iglu:com.elevio/ticket_resolution_start/jsonschema/1-0-0',
    {
      ticket_id: ticketId,
    },
    url,
    languageId,
  );
  ampli.knowledgeResolutionInitialised({
    conversationId: ticketId,
    knowledgeSource: 'elevio',
  });
}

// First, define a type that, when passed a union of keys, creates an object which
// cannot have those properties. I couldn't find a way to use this type directly,
// but it can be used with the below type.
type Impossible<K extends keyof any> = {
  [P in K]: never;
};

// The secret sauce! Provide it the type that contains only the properties you want,
// and then a type that extends that type, based on what the caller provided
// using generics.
type NoExtraProperties<T, U extends T = T> = U &
  Impossible<Exclude<keyof U, keyof T>>;

export type TicketResolvedEvent = CommonEventParams & {
  helpfulArticleId: string;
  ticketId: string;
  ticketThread: ReadonlyArray<NoExtraProperties<Thread>>;
  suggestedArticleIds: ReadonlyArray<string>;
  ticketTags: ReadonlyArray<string>;
  ticketSubject: TicketSubject;
};
/**
 * When a ticket is resolved this event is fired, so this happens before the
 * final confirmation modal is shown.
 *
 * @param helpfulArticleId
 * @param ticketId
 * @param ticketThread
 * @param suggestedArticleIds
 * @param ticketTags
 */
export function ticketResolved({
  helpfulArticleId,
  ticketId,
  ticketThread,
  suggestedArticleIds,
  ticketTags,
  ticketSubject,
  url,
  languageId,
}: TicketResolvedEvent): void {
  const suggested_article_ids =
    removeAnchorFromMultipleArticleIds(suggestedArticleIds);
  const helpful_article_ids = removeAnchorFromMultipleArticleIds([
    helpfulArticleId,
  ]);

  trackSelfDescribingEvent(
    'iglu:com.elevio/ticket_resolution/jsonschema/1-0-2',
    {
      ticket_id: ticketId,
      ticket_thread: ticketThread,
      ticket_subject: (ticketSubject || '').substring(0, 499),
      ticket_tags: ticketTags,
      suggested_article_ids,
      helpful_article_ids,
    },
    url,
    languageId,
  );
  ampli.conversationResolvedByKnowledge({
    conversationId: ticketId,
    helpfulArticleIds: helpful_article_ids,
    knowledgeSource: 'elevio',
    suggestedArticleIds: suggested_article_ids,
  });
}

export type TicketResolutionExitEvent = CommonEventParams & {
  ticketId: string;
};
/**
 * TicketResolutionExit is fired when the completion modal is closed without
 * completing the flow. ie, the 'X' modal close button is pressed.
 *
 * @param ticketId
 * @param url
 */
export function ticketResolutionExit({
  ticketId,
  url,
  languageId,
}: TicketResolutionExitEvent): void {
  trackSelfDescribingEvent(
    'iglu:com.elevio/ticket_resolution_exit/jsonschema/1-0-0',
    {
      ticket_id: ticketId,
    },
    url,
    languageId,
  );
  ampli.knowledgeResolutionCancelled({
    conversationId: ticketId,
    knowledgeSource: 'elevio',
  });
}

export type TicketNonResolutionEvent = {
  ticketId: string;
  ticketThread: ReadonlyArray<NoExtraProperties<Thread>>;
  suggestedArticleIds: ReadonlyArray<string>;
  ticketTags: ReadonlyArray<string>;
  ticketSubject: TicketSubject;
  url: string;
  languageId: string;
};
export function ticketNonResolution({
  ticketId,
  ticketThread,
  suggestedArticleIds,
  ticketTags,
  ticketSubject,
  url,
  languageId,
}: TicketNonResolutionEvent): void {
  const suggested_article_ids =
    removeAnchorFromMultipleArticleIds(suggestedArticleIds);

  trackSelfDescribingEvent(
    'iglu:com.elevio/ticket_non_resolution/jsonschema/1-0-2',
    {
      ticket_id: ticketId,
      ticket_thread: ticketThread,
      ticket_subject: (ticketSubject || '').substring(0, 499),
      ticket_tags: ticketTags,
      suggested_article_ids,
    },
    url,
    languageId,
  );
  ampli.conversationResolvedWithNoKnowledgeArticle({
    knowledgeSource: 'elevio',
    conversationId: ticketId,
    suggestedArticleIds: suggested_article_ids,
  });
}

type AssistType = 'copy' | 'helpful_click';

type TicketAssist<T extends Thread> = CommonEventParams & {
  helpfulArticleIds: Array<string>;
  ticketId: string;
  ticketThread: ReadonlyArray<NoExtraProperties<Thread, T>>;
  suggestedArticleIds: ReadonlyArray<string>;
  ticketTags: ReadonlyArray<string>;
  assistType: AssistType;
  ticketSubject: TicketSubject;
};
export type TicketAssistWithArticleEvent = TicketResolvedEvent & {
  articleTitle: string;
};
export type ArticleTicketAssistEvent = Pick<
  TicketAssist<any>,
  | 'assistType'
  | 'ticketId'
  | 'ticketThread'
  | 'suggestedArticleIds'
  | 'ticketSubject'
  | 'url'
> & {
  articleId: string;
  articleTags: string[];
};

/**
 * This is send we a user click on "This article helped" or they copy text from
 * an article.
 *
 * @param helpfulArticleId
 * @param ticketId
 * @param ticketThread
 * @param suggestedArticleIds
 * @param ticketTags
 * @param ticketTags
 * @param assistType
 */
export function ticketAssist<T extends Thread>({
  helpfulArticleIds,
  ticketId,
  ticketThread,
  suggestedArticleIds,
  ticketTags,
  assistType,
  ticketSubject,
  url,
  languageId,
}: TicketAssist<T>): void {
  const suggested_article_ids =
    removeAnchorFromMultipleArticleIds(suggestedArticleIds);
  const helpful_article_ids =
    removeAnchorFromMultipleArticleIds(helpfulArticleIds);

  trackSelfDescribingEvent(
    'iglu:com.elevio/ticket_assist/jsonschema/1-0-2',
    {
      ticket_id: ticketId,
      ticket_thread: ticketThread,
      ticket_subject: (ticketSubject || '').substring(0, 499),
      ticket_tags: ticketTags,
      suggested_article_ids,
      helpful_article_ids,
      assist_type: assistType,
    },
    url,
    languageId,
  );
  // When a user chooses an article that helped to resolve a conversation
  ampli.knowledgeArticleMarkedAsHelpful({
    knowledgeSource: 'elevio',
    conversationId: ticketId,
    suggestedArticleIds: suggested_article_ids,
    helpfulArticleIds: helpful_article_ids,
    assistType, // copy|helpful_click
  });
}

export type ArticleViewEvent = CommonEventParams & {
  articleId: string;
  ticketTags: ReadonlyArray<string>;
  ticketStatus: Maybe<string>;
  ticketId: string;
};

export function articleView({
  articleId,
  ticketTags,
  ticketStatus,
  url,
  ticketId,
  languageId,
}: ArticleViewEvent): void {
  const article_id = removeAnchorFromArticleId(articleId);

  trackSelfDescribingEvent(
    'iglu:com.elevio/article_view/jsonschema/1-0-1',
    {
      ticket_id: ticketId,
      ticket_tags: ticketTags,
      ticket_status: ticketStatus || 'unknown',
      article_id,
    },
    url,
    languageId,
  );
  ampli.knowledgeArticleViewed({
    knowledgeSource: 'elevio',
    articleId,
    conversationId: ticketId,
    source: 'conversation-ui',
  });
}

export type ArticleFeedbackEvent = CommonEventParams & {
  articleId: string;
  articleLastPublishedAt: string; // ISO format
  ticketId: string;
  feedbackText: string;
};

export function articleFeedback({
  articleId,
  articleLastPublishedAt,
  ticketId,
  feedbackText,
  url,
  languageId,
}: ArticleFeedbackEvent): void {
  const article_id = removeAnchorFromArticleId(articleId);

  trackSelfDescribingEvent(
    'iglu:com.elevio/article_feedback/jsonschema/1-0-1',
    {
      ticket_id: ticketId,
      article_last_published_at: articleLastPublishedAt,
      article_id,
      feedback_text: feedbackText,
    },
    url,
    languageId,
  );
  ampli.knowledgeArticleFeedbackSubmitted({
    articleId,
    articleLastPublishedAt,
    conversationId: ticketId,
    knowledgeSource: 'elevio',
    source: 'conversation-ui',
  });
}

export type ArticleDraft = CommonEventParams & {
  ticketId: string;
  ticketTitle: Maybe<string>;
  ticketThread: ConversationThread;
};

export function articleDraft({
  ticketId,
  ticketTitle,
  ticketThread,
  url,
  languageId,
}: ArticleDraft): void {
  trackSelfDescribingEvent(
    'iglu:com.elevio/article_draft/jsonschema/1-0-0',
    {
      ticket_id: ticketId,
      ticket_title: ticketTitle || 'Untitled Ticket',
      ticket_thread: ticketThread,
    },
    url,
    languageId,
  );
  ampli.conversationResolvedAsAKnowledgeArticleDraft({
    conversationId: ticketId,
    knowledgeSource: 'elevio',
  });
}

export type KnowledgeGapEvent = CommonEventParams & {
  ticketId: string;
  ticketTags: ReadonlyArray<string>;
  suggestedArticleIds: ReadonlyArray<string>;
};
export function knowledgeGap({
  ticketId,
  ticketTags,
  suggestedArticleIds,
  url,
  languageId,
}: KnowledgeGapEvent): void {
  const suggested_article_ids =
    removeAnchorFromMultipleArticleIds(suggestedArticleIds);

  trackSelfDescribingEvent(
    'iglu:com.elevio/knowledge_gap/jsonschema/1-0-1',
    {
      ticket_id: ticketId,
      ticket_tags: ticketTags,
      suggested_article_ids,
    },
    url,
    languageId,
  );
  ampli.conversationResolvedAsAKnowledgeGap({
    conversationId: ticketId,
    suggestedArticleIds: suggested_article_ids,
    knowledgeSource: 'elevio',
  });
}

type TicketView = {
  ticketId: string;
  status: string;
  url: string | undefined;
  languageId: string;
};
export function ticketView({
  ticketId,
  status,
  url,
  languageId,
}: TicketView): void {
  trackSelfDescribingEvent(
    'iglu:com.elevio/ticket_view/jsonschema/1-0-0',
    {
      ticket_id: ticketId,
      ticket_status: status,
    },
    url,
    languageId,
  );
  // No `analytics` event needed here as it is already tracked elsewhere
}
